import { useEffect, useState } from 'react';
import type { AssignInvoiceInput, Invoice } from '@faktorplus/shared';
import { useAssignInvoice, useProjectSuggestion } from '../../api/invoices';
import { useCostCategories, useProjects, useSiteManagers } from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { Field, Input, Select } from '../../components/ui/form';
import { ErrorBox } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import pageStyles from '../../components/layout/page.module.css';
import styles from './assign-invoice-form.module.css';

interface AssignInvoiceFormProps {
  invoice: Invoice;
  onSaved: () => void;
  /** Rendered to the left of the submit button; the queue uses it for "Pomiń". */
  secondaryAction?: React.ReactNode;
  submitLabel?: string;
}

/**
 * Booking an invoice to a project, a manager and a category.
 *
 * Shared deliberately: the accountant hits this from the "do przypisania"
 * queue, but also from any single invoice opened out of the register — an
 * invoice that can only be assigned from one particular screen is a dead end.
 */
export function AssignInvoiceForm({
  invoice,
  onSaved,
  secondaryAction,
  submitLabel = 'Zapisz przypisanie',
}: AssignInvoiceFormProps) {
  const { data: projects = [] } = useProjects();
  const { data: managers = [] } = useSiteManagers();
  const { data: categories = [] } = useCostCategories();
  const { data: suggestion } = useProjectSuggestion(invoice.vendorId);
  const assign = useAssignInvoice();

  const allocation = invoice.allocations[0];

  // Re-assigning a disputed invoice starts from what it already had, not blank.
  const [projectId, setProjectId] = useState(allocation?.projectId ?? '');
  const [managerId, setManagerId] = useState(allocation?.managerId ?? '');
  const [categoryId, setCategoryId] = useState(invoice.categoryId ?? '');
  const [dueDate, setDueDate] = useState(toDateInput(invoice.dueDate));
  const [requiresProtocol, setRequiresProtocol] = useState(invoice.requiresHandoverProtocol);
  const [notifyManager, setNotifyManager] = useState(true);

  // Pre-fill from where this vendor's recent invoices were booked. The
  // accountant confirms rather than researches.
  useEffect(() => {
    if (!suggestion) return;
    setProjectId((current) => current || suggestion.projectId);
    setManagerId((current) => current || (suggestion.managerId ?? ''));
  }, [suggestion]);

  // A subcontractor bill almost always needs a handover protocol.
  useEffect(() => {
    if (invoice.vendorType === 'SUBCONTRACTOR') setRequiresProtocol(true);
  }, [invoice.vendorType]);

  // Without this flag, picking "— bez kierownika" would immediately snap back
  // to the project's default, because both are the empty string.
  const [managerTouched, setManagerTouched] = useState(Boolean(allocation?.managerId));

  const defaultManagerForProject = managers.find((manager) =>
    manager.projectIds.includes(projectId),
  );
  const effectiveManagerId = managerTouched ? managerId : (defaultManagerForProject?.id ?? '');
  const hasManager = Boolean(effectiveManagerId);

  async function handleSave() {
    const input: AssignInvoiceInput = {
      projectId: projectId || null,
      managerId: effectiveManagerId || null,
      categoryId,
      dueDate: dueDate ? new Date(dueDate) : null,
      // Booking an invoice says nothing about retention, so this screen passes
      // through whatever the invoice already carries. Sending 0 instead would
      // silently raise the amount due on every re-assignment.
      retentionAmount: invoice.retentionAmount,
      requiresHandoverProtocol: requiresProtocol,
      notifyManager,
    };

    await assign.mutateAsync({ id: invoice.id, input });
    onSaved();
  }

  const isSuggested = Boolean(suggestion && projectId === suggestion.projectId);

  return (
    <div className={styles.form}>
      <Field
        label="Budowa"
        hint={
          isSuggested && suggestion ? (
            <>
              Podpowiedź: <b>
                {suggestion.matchCount} z ostatnich {suggestion.sampleSize}
              </b>{' '}
              faktur od {invoice.vendorName} szło na {suggestion.projectName}.
            </>
          ) : undefined
        }
      >
        {(id) => (
          <Select
            id={id}
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
              setManagerId('');
              setManagerTouched(false);
            }}
          >
            <option value="">— koszt ogólny firmy</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Kierownik">
        {(id) => (
          <Select
            id={id}
            value={effectiveManagerId}
            onChange={(event) => {
              setManagerId(event.target.value);
              setManagerTouched(true);
            }}
          >
            <option value="">— bez kierownika</option>
            {managers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.fullName}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {!hasManager && (
        <p className={styles.checkNote}>
          Koszt ogólny bez kierownika trafi bezpośrednio do zatwierdzenia przez właściciela.
        </p>
      )}

      <Field label="Kategoria">
        {(id) => (
          <Select id={id} value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            <option value="">— wybierz kategorię</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Termin płatności">
        {(id) => (
          <Input
            id={id}
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        )}
      </Field>

      <div className={styles.checks}>
        <label className={styles.check}>
          <input
            type="checkbox"
            className={pageStyles.checkbox}
            checked={requiresProtocol}
            onChange={(event) => setRequiresProtocol(event.target.checked)}
          />
          <span>
            Wymaga protokołu odbioru
            <span className={styles.checkNote}>termin liczony od kompletu dokumentów</span>
          </span>
        </label>

        <label className={styles.check}>
          <input
            type="checkbox"
            className={pageStyles.checkbox}
            checked={notifyManager && hasManager}
            disabled={!hasManager}
            onChange={(event) => setNotifyManager(event.target.checked)}
          />
          <span>
            Powiadom kierownika mailem
            {!hasManager && <span className={styles.checkNote}>nie dotyczy kosztu ogólnego</span>}
          </span>
        </label>
      </div>

      {assign.error && (
        <ErrorBox>
          {assign.error instanceof ApiError ? assign.error.message : 'Nie udało się zapisać.'}
        </ErrorBox>
      )}

      <div className={styles.footer}>
        {secondaryAction}
        <span className={styles.footerSpacer} />
        <Button
          variant="primary"
          disabled={!categoryId}
          isLoading={assign.isPending}
          onClick={() => void handleSave()}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function toDateInput(value: Date | string | null): string {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}
